/**
 * Shared styled components for the public workshop report.
 * Sitra brand: pure black/white core, Rational typeface, yellow accents.
 * Print-optimized, responsive.
 */
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { EVALUATION_TAG_META } from '@kuutio/shared/evaluationTags.js';

// Sitra brand tokens — colocated for easy iteration
export const SITRA = {
  black: '#000000',
  white: '#FFFFFF',
  yellow: '#FFD93D',     // primary accent
  orange: '#fa6129',     // signature warm — generic accent
  green: '#00613d',      // dark green — opportunity / positive (alias of havu)
  blue: '#367ABA',       // Sitra blue — used for innovation / data
  purple: '#7c3aed',     // AI accent (per brand-guide)
  amber: '#7a3a0c',      // dark amber — evidence / research
  // Sitra brand-book reds/greens — käsikirjoitus 2026-05:
  // "Punainen Sitran puolukalla ja vihreä havulla"
  puolukka: '#E8114B',         // Sitra puolukka — official "este" red
  puolukkaBg: 'rgba(232, 17, 75, 0.08)',
  havu: '#00613d',             // Sitra havu (dark spruce) — official "mahdollisuus" green
  havuBg: '#ecfdf5',
  textBody: '#1a1a1a',
  textMuted: '#4d4d4d',
  border: '#e5e5e5',
  surfaceMuted: '#f5f5f5',
};

// Unified metric-tag config — single source of truth for the
// Vaik/Tot/Uutuus/Näyttö pills used in ReportSolutions, ReportHybridSolutions,
// SolutionAnalysis. Matches Sitra palette + lucide icons.
// Import lucide icons separately at the call site.
export const METRIC_STYLES = {
  impact:      { color: SITRA.purple, bg: '#f5f3ff', border: '#ddd6fe', label: 'Vaikuttavuus', shortLabel: 'Vaik.' },
  feasibility: { color: SITRA.green,  bg: '#ecfdf5', border: '#a7f3d0', label: 'Toteutettavuus', shortLabel: 'Tot.' },
  innovation:  { color: SITRA.blue,   bg: '#f5f5f5', border: '#bfdbfe', label: 'Uutuusarvo',  shortLabel: 'Uutuus' },
  evidence:    { color: SITRA.amber,  bg: '#fffbeb', border: '#fde68a', label: 'Tutkimusnäyttö', shortLabel: 'Näyttö' },
};

/** Light-theme tokens for solution evaluation tag chips (v2). Legacy METRIC_STYLES kept for old analyses. */
export const TAG_STYLES = {
  ...Object.fromEntries(
    Object.entries(EVALUATION_TAG_META).map(([id, m]) => [
      id,
      { color: m.color, bg: m.bgLight, border: m.borderLight },
    ]),
  ),
  contradicts_research: {
    color: SITRA.orange,
    bg: '#fff1eb',
    border: '#fecaca',
  },
};

// ============================================================================
// LAYOUT
// ============================================================================

export const Container = styled.div`
  min-height: 100vh;
  background: ${SITRA.white};
  color: ${SITRA.textBody};
  font-family: 'Rational', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  /* Block horizontal overflow at the outer shell — child grids with
   * minmax columns or wide tables can spill out without this on
   * narrow viewports. Mobile rotation revealed it. */
  overflow-x: hidden;

  @media print {
    background: #fff;
    width: 100%;
    max-width: none;
    margin: 0;
    padding: 0;
  }

  @page {
    size: A4;
    margin: 15mm 12mm;
  }
`;

export const Main = styled.main`
  max-width: 900px;
  margin: 0 auto;
  padding: 48px 32px;
  /* Trim side padding on narrow viewports so cards have room.
   * 32 → 16 → 12 as width drops. */
  @media (max-width: 720px) { padding: 32px 16px; }
  @media (max-width: 480px) { padding: 24px 12px; }

  @media print {
    max-width: 100%;
    padding: 24px 0;
    margin: 0;
  }
`;

export const Section = styled.section`
  margin-bottom: 64px;

  @media print {
    page-break-inside: avoid;
  }
`;

export const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${SITRA.border};
  margin: 48px 0;
`;

// ============================================================================
// TYPOGRAPHY
// ============================================================================

export const SectionTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: ${SITRA.black};
  margin: 0 0 24px 0;
  padding-bottom: 12px;
  border-bottom: 4px solid ${SITRA.black};
  display: flex;
  align-items: center;
  gap: 12px;
  letter-spacing: -0.01em;

  svg {
    flex-shrink: 0;
    color: ${SITRA.black};
  }
`;

export const SectionSubtitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${SITRA.black};
  margin: 32px 0 16px 0;
  letter-spacing: -0.01em;
`;

export const Paragraph = styled.p`
  font-size: 16px;
  line-height: 1.7;
  color: ${SITRA.textBody};
  margin: 0 0 16px 0;
`;

// ============================================================================
// CARDS & TABLES
// ============================================================================

export const Card = styled.div`
  background: ${SITRA.white};
  border: 1px solid ${SITRA.border};
  border-radius: 4px;
  padding: 24px;
  margin: 24px 0;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 24px 0;
  font-size: 14px;
`;

export const Th = styled.th`
  background: ${SITRA.black};
  color: ${SITRA.white};
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;

  @media print {
    background: ${SITRA.black} !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
`;

export const Td = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid ${SITRA.border};
  vertical-align: top;
`;

export const Tr = styled.tr`
  &:nth-of-type(even) {
    background: ${SITRA.surfaceMuted};
  }
`;

// ============================================================================
// STATS
// ============================================================================

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 16px;
  margin: 24px 0;
`;

export const StatCard = styled.div`
  background: ${props => props.$bg || SITRA.yellow};
  padding: 20px;
  border-radius: 4px;
  text-align: center;

  @media print {
    background: ${props => props.$bg || SITRA.yellow} !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
`;

export const StatValue = styled.div`
  font-size: 32px;
  font-weight: 800;
  color: ${props => props.$color || SITRA.black};
  letter-spacing: -0.02em;
`;

export const StatLabel = styled.div`
  font-size: 14px;
  color: ${SITRA.black};
  margin-top: 4px;
  font-weight: 500;
`;

// ============================================================================
// BAR CHARTS
// ============================================================================

export const BarChart = styled.div`
  margin: 24px 0;
`;

export const BarRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

export const BarLabel = styled.div`
  width: 280px;
  font-size: 14px;
  color: #444;
  flex-shrink: 0;
  line-height: 1.3;
`;

export const BarTrack = styled.div`
  flex: 1;
  height: 28px;
  background: #f3f4f6;
  border-radius: 4px;
  overflow: hidden;
`;

export const BarFill = styled.div`
  height: 100%;
  background: ${props => props.$color || '#3b82f6'};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 10px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  width: ${props => props.$width}%;
  min-width: 40px;
  transition: width 0.5s ease;

  @media print {
    background: ${props => props.$color || '#3b82f6'} !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
`;

// ============================================================================
// QUOTES
// ============================================================================

export const Quote = styled.blockquote`
  background: #f0f7ff;
  border-left: 4px solid #3b82f6;
  padding: 16px 20px;
  margin: 16px 0;
  border-radius: 0 8px 8px 0;
  font-style: italic;
  color: #1e40af;
  font-size: 15px;
  line-height: 1.7;

  @media print {
    background: #f0f7ff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
`;

// ============================================================================
// GRAPH (D3)
// ============================================================================

export const GraphContainer = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  margin: 24px 0;

  svg {
    display: block;
    margin: 0 auto;
  }

  @media print {
    border: 1px solid #ccc;
    page-break-inside: avoid;
  }
`;

export const GraphLegend = styled.div`
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-top: 16px;
  flex-wrap: wrap;
`;

export const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666;
`;

export const LegendDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.$color};
`;

// ============================================================================
// LOADING / ERROR
// ============================================================================

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const LoadingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
`;

export const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

export const LoadingText = styled.p`
  margin-top: 16px;
  color: #666;
  font-size: 16px;
`;

export const ErrorContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fef2f2;
  color: #991b1b;
  padding: 32px;
  text-align: center;
`;

// ============================================================================
// SCORE BADGE (shared by ReportSolutions + ReportHybridSolutions)
// ============================================================================

export const HeaderScore = styled.span`
  font-size: 14px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 10px;
  white-space: nowrap;
  flex-shrink: 0;
  background: ${p => p.$score >= 1 ? '#ecfdf5' : '#fffbeb'};
  color: ${p => p.$score >= 1 ? '#059669' : '#f59e0b'};

  @media print {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
`;

// ============================================================================
// FOOTER
// ============================================================================

export const Footer = styled.footer`
  background: #1a1a2e;
  color: rgba(255, 255, 255, 0.7);
  padding: 36px 24px 32px;
  text-align: center;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  /* Sitra wordmark — the real /assets/sitra_logo_musta.svg asset,
   * inverted to white (SVG ships in brand black). */
  .sitra-wordmark-img {
    height: 32px;
    width: auto;
    filter: brightness(0) invert(1);
    display: block;
  }

  /* Sitra.fi link — white at low opacity so it pairs with the white
   * wordmark above rather than competing with it. */
  a.sitra-link {
    color: rgba(255, 255, 255, 0.7);
    text-decoration: none;
    font-size: 13px;
    letter-spacing: 1px;
    &:hover { color: #fff; text-decoration: underline; }
  }

  .meta {
    color: rgba(255, 255, 255, 0.45);
    font-size: 12.5px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .meta img {
    height: 16px;
    width: 16px;
    vertical-align: middle;
    opacity: 0.85;
  }

  @media print {
    display: none;
  }
`;
